import { useState } from "react";
import axios from "axios";
import { Phone, MessageCircle, Mail, MapPin, Clock, Loader2, Navigation } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";
import { HOTEL, formatApiErrorDetail } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const labelCls = "mb-2 block text-[10px] font-medium uppercase tracking-[0.28em] text-[#8C713B]";
const inputCls =
  "w-full border-b border-[#2A2421] bg-transparent py-2 text-sm text-[#F5F2EB] outline-none transition-colors focus:border-[#C5A059]";

const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        phone: form.phone || null,
        email: form.email || null,
        message: form.message,
      });
      toast.success("Message sent. We will be in touch shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send your message. Please call us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-[#2A2421] bg-[#161311] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="mb-14">
            <p className="text-[11px] font-medium uppercase tracking-[0.35em] text-[#C5A059]">Find Us</p>
            <h2 className="mt-4 font-display text-4xl font-light text-[#F5F2EB] sm:text-5xl">Location & Contact</h2>
          </div>
        </Reveal>

        <div className="grid gap-6 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div className="flex h-full flex-col justify-between border border-[#2A2421] bg-[#0D0B0A] p-8 sm:p-10">
              <div>
                <h3 className="font-display text-2xl text-[#F5F2EB]">The Rayan</h3>
                <p data-testid="contact-address" className="mt-4 flex items-start gap-3 text-sm leading-relaxed text-[#A8A29E]">
                  <MapPin size={16} className="mt-0.5 shrink-0 text-[#C5A059]" />
                  {HOTEL.address}
                </p>
                <p className="mt-4 flex items-center gap-3 text-sm text-[#A8A29E]">
                  <Clock size={16} className="shrink-0 text-[#C5A059]" /> Front desk open 24 × 7
                </p>
              </div>
              <div className="mt-10 space-y-3">
                <a
                  data-testid="contact-call-button"
                  href={HOTEL.phoneLink}
                  className="flex items-center justify-center gap-3 border border-[#C5A059] bg-[#C5A059] px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#0D0B0A] transition-all duration-300 hover:bg-transparent hover:text-[#C5A059]"
                >
                  <Phone size={15} /> {HOTEL.phoneDisplay}
                </a>
                <a
                  data-testid="contact-whatsapp-button"
                  href={HOTEL.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-3 border border-[#2A2421] px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#F5F2EB] transition-all duration-300 hover:border-[#C5A059] hover:text-[#C5A059]"
                >
                  <MessageCircle size={15} /> WhatsApp Us
                </a>
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${HOTEL.email}`}
                  className="flex items-center justify-center gap-3 px-6 py-2 text-xs tracking-wider text-[#A8A29E] transition-colors hover:text-[#C5A059]"
                >
                  <Mail size={14} /> {HOTEL.email}
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-3">
            <form data-testid="contact-form" onSubmit={submit} className="h-full border border-[#2A2421] bg-[#0D0B0A] p-8 sm:p-10">
              <h3 className="font-display text-2xl text-[#F5F2EB]">Write to Us</h3>
              <div className="mt-6 grid gap-6 sm:grid-cols-2">
                <div>
                  <label className={labelCls} htmlFor="ct-name">Name</label>
                  <input id="ct-name" data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Your name" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="ct-phone">Phone</label>
                  <input id="ct-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="+91 ..." />
                </div>
              </div>
              <div className="mt-6">
                <label className={labelCls} htmlFor="ct-email">Email</label>
                <input id="ct-email" data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@email.com" />
              </div>
              <div className="mt-6">
                <label className={labelCls} htmlFor="ct-message">Message</label>
                <textarea id="ct-message" data-testid="contact-message-input" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} placeholder="How can we help?" />
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="mt-8 flex items-center justify-center gap-2 border border-[#C5A059] bg-[#C5A059] px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#0D0B0A] transition-all duration-300 hover:bg-transparent hover:text-[#C5A059] disabled:opacity-60"
              >
                {sending && <Loader2 size={15} className="animate-spin" />}
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-6">
          <div className="relative overflow-hidden border border-[#2A2421]" data-testid="location-map">
            <iframe
              title="The Rayan — Hyderpora Bypass, Srinagar"
              src={HOTEL.mapEmbed}
              className="h-[380px] w-full"
              style={{ filter: "grayscale(1) brightness(0.85) sepia(0.15)" }}
              loading="lazy"
            />
            <a
              data-testid="get-directions-button"
              href={HOTEL.mapsLink}
              target="_blank"
              rel="noreferrer"
              className="absolute bottom-5 right-5 flex items-center gap-2 border border-[#C5A059] bg-[#0D0B0A]/90 px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C5A059] backdrop-blur transition-all duration-300 hover:bg-[#C5A059] hover:text-[#0D0B0A]"
            >
              <Navigation size={13} /> Get Directions
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
